// 认证状态 store（localStorage 持久化；SSR-safe）
// 与扩展端 main/src/stores/auth.ts 语义对齐（token/tokenHash/expiresAt/userInfo）

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AuthResponse, UserInfo } from '@/types/auth'

const AUTH_STORAGE_KEY = 'yfb.auth'

export { AUTH_STORAGE_KEY }

export const useAuthStore = defineStore('auth', () => {
  const token = ref('')
  const tokenHash = ref('')
  const expiresAt = ref(0) // 毫秒时间戳
  const userInfo = ref<UserInfo | null>(null)

  // client-only：登录态持久化到 localStorage（SSR 期间跳过）。
  // 注：登录同步给浏览器扩展已不再走 postMessage 广播（ADR-0028 删桥）——扩展登录由
  // 一次性 code 交接 + SW redeem 单轨完成；网页先登录、扩展后点登录时由扩展发起
  // handoff 自动补齐。localStorage 仍保留供本站 hydrate 与旧兼容。
  const isLoggedIn = computed(() => {
    if (!token.value || !expiresAt.value) return false
    return Date.now() < expiresAt.value
  })

  const isExpired = computed(() => !!token.value && expiresAt.value > 0 && Date.now() >= expiresAt.value)

  function setAuth(data: AuthResponse) {
    token.value = data.token
    tokenHash.value = data.token_hash || ''
    expiresAt.value = Date.now() + data.expires_in * 1000
    userInfo.value = data.user_info
  }

  function setUserInfo(info: UserInfo | null) {
    userInfo.value = info
    if (info) token.value && persist()
  }

  // 滑动续期（后端 active_extend 轮换）：采纳响应头下发的 token / token_hash / 有效期，
  // 活跃用户无需重新登录即自动续期；7 天不活跃则由后端判定过期（401 → 拦截器登出）。
  function applyRotatedToken(data: { token: string; tokenHash?: string; expiresIn?: number }) {
    if (!data.token) return
    token.value = data.token
    if (data.tokenHash) tokenHash.value = data.tokenHash
    if (data.expiresIn && data.expiresIn > 0) {
      expiresAt.value = Date.now() + data.expiresIn * 1000
    }
    persist()
  }

  function clear() {
    token.value = ''
    tokenHash.value = ''
    expiresAt.value = 0
    userInfo.value = null
    persist()
  }

  // client-only：localStorage 读写（vite-ssg SSR 期间跳过）
  function persist() {
    if (typeof window === 'undefined') return
    try {
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          token: token.value,
          tokenHash: tokenHash.value,
          expiresAt: expiresAt.value,
          userInfo: userInfo.value
        })
      )
    } catch {
      // 隐私模式/配额满 → 忽略
    }
  }

  function hydrate() {
    if (typeof window === 'undefined') return
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY)
      if (!raw) return
      const data = JSON.parse(raw) as {
        token?: string
        tokenHash?: string
        expiresAt?: number
        userInfo?: UserInfo | null
      }
      token.value = data.token || ''
      tokenHash.value = data.tokenHash || ''
      expiresAt.value = data.expiresAt || 0
      userInfo.value = data.userInfo || null
    } catch {
      clear()
    }
  }

  return {
    token,
    tokenHash,
    expiresAt,
    userInfo,
    isLoggedIn,
    isExpired,
    setAuth,
    setUserInfo,
    applyRotatedToken,
    clear,
    hydrate,
    persist
  }
})
